/**
 * Das Muskelmodell der Trainingsseite (F-34): 35 Detailmuskeln, die sich zu
 * 18 Uebersichtsgruppen zusammensetzen.
 *
 * Zaehlregel (stimmt auf beiden Stufen ueberein): Ein Satz zaehlt fuer einen
 * Muskel voll, wenn er Hauptmuskel der Uebung ist, halb als Hilfsmuskel. Eine
 * Gruppe bekommt je Satz den hoechsten Wert ihrer Muskeln - nicht die Summe,
 * sonst zaehlte Bankdruecken fuer „Brust“ dreifach.
 *
 * `groesse` geht in die Regenerations-Schaetzung ein (grosse Muskeln brauchen
 * laenger), `region` in den Gesamtrang (Ober-/Unterkoerper, Rumpf).
 */
export type Region = 'oberkoerper' | 'unterkoerper' | 'rumpf';
export type Groesse = 'gross' | 'mittel' | 'klein';

export const GRUPPEN = {
  brust: { label: 'Brust', region: 'oberkoerper' },
  schulter_vorne: { label: 'Vordere Schulter', region: 'oberkoerper' },
  schulter_seite: { label: 'Seitliche Schulter', region: 'oberkoerper' },
  schulter_hinten: { label: 'Hintere Schulter', region: 'oberkoerper' },
  bizeps: { label: 'Bizeps', region: 'oberkoerper' },
  trizeps: { label: 'Trizeps', region: 'oberkoerper' },
  unterarme: { label: 'Unterarme', region: 'oberkoerper' },
  trapez: { label: 'Oberer Rücken / Trapez', region: 'oberkoerper' },
  latissimus: { label: 'Latissimus', region: 'oberkoerper' },
  unterer_ruecken: { label: 'Unterer Rücken', region: 'rumpf' },
  bauch: { label: 'Bauch', region: 'rumpf' },
  seitlicher_bauch: { label: 'Seitliche Bauchmuskeln', region: 'rumpf' },
  gesaess: { label: 'Gesäß', region: 'unterkoerper' },
  quadrizeps: { label: 'Quadrizeps', region: 'unterkoerper' },
  beinbeuger: { label: 'Beinbeuger', region: 'unterkoerper' },
  adduktoren: { label: 'Adduktoren', region: 'unterkoerper' },
  waden: { label: 'Waden', region: 'unterkoerper' },
  hueftbeuger: { label: 'Hüftbeuger', region: 'unterkoerper' },
} as const satisfies Record<string, { label: string; region: Region }>;

export type GruppeId = keyof typeof GRUPPEN;

export const MUSKELN = {
  brust_oben: { label: 'Brust oben', gruppe: 'brust', groesse: 'mittel' },
  brust_mitte: { label: 'Brust Mitte', gruppe: 'brust', groesse: 'gross' },
  brust_unten: { label: 'Brust unten', gruppe: 'brust', groesse: 'mittel' },
  serratus: { label: 'Sägemuskel (Serratus)', gruppe: 'brust', groesse: 'klein' },
  schulter_vorne: { label: 'Schulter vorne', gruppe: 'schulter_vorne', groesse: 'klein' },
  schulter_seite: { label: 'Schulter seitlich', gruppe: 'schulter_seite', groesse: 'klein' },
  schulter_hinten: { label: 'Schulter hinten', gruppe: 'schulter_hinten', groesse: 'klein' },
  rotatoren: { label: 'Rotatorenmanschette', gruppe: 'schulter_hinten', groesse: 'klein' },
  trapez_oben: { label: 'Trapez oben', gruppe: 'trapez', groesse: 'mittel' },
  trapez_mitte: { label: 'Trapez Mitte', gruppe: 'trapez', groesse: 'mittel' },
  trapez_unten: { label: 'Trapez unten', gruppe: 'trapez', groesse: 'mittel' },
  rhomboiden: { label: 'Rautenmuskeln (Rhomboiden)', gruppe: 'trapez', groesse: 'mittel' },
  latissimus: { label: 'Latissimus', gruppe: 'latissimus', groesse: 'gross' },
  teres_major: { label: 'Großer Rundmuskel (Teres major)', gruppe: 'latissimus', groesse: 'klein' },
  rueckenstrecker: { label: 'Rückenstrecker', gruppe: 'unterer_ruecken', groesse: 'gross' },
  bauch_gerade: { label: 'Gerader Bauchmuskel', gruppe: 'bauch', groesse: 'mittel' },
  bauch_schraeg: { label: 'Schräge Bauchmuskeln', gruppe: 'seitlicher_bauch', groesse: 'mittel' },
  bizeps: { label: 'Bizeps', gruppe: 'bizeps', groesse: 'klein' },
  brachialis: { label: 'Oberarmmuskel (Brachialis)', gruppe: 'bizeps', groesse: 'klein' },
  trizeps_lang: { label: 'Trizeps, langer Kopf', gruppe: 'trizeps', groesse: 'klein' },
  trizeps_seitlich: { label: 'Trizeps, seitlicher Kopf', gruppe: 'trizeps', groesse: 'klein' },
  unterarm_beuger: { label: 'Unterarmbeuger', gruppe: 'unterarme', groesse: 'klein' },
  unterarm_strecker: { label: 'Unterarmstrecker', gruppe: 'unterarme', groesse: 'klein' },
  gesaess_gross: { label: 'Großer Gesäßmuskel', gruppe: 'gesaess', groesse: 'gross' },
  gesaess_mittel: { label: 'Mittlerer Gesäßmuskel', gruppe: 'gesaess', groesse: 'mittel' },
  abduktoren: { label: 'Abduktoren', gruppe: 'gesaess', groesse: 'klein' },
  hueftbeuger: { label: 'Hüftbeuger', gruppe: 'hueftbeuger', groesse: 'mittel' },
  adduktoren: { label: 'Adduktoren', gruppe: 'adduktoren', groesse: 'mittel' },
  quad_gerade: { label: 'Quadrizeps, gerader Anteil', gruppe: 'quadrizeps', groesse: 'gross' },
  quad_innen: { label: 'Quadrizeps, innerer Anteil', gruppe: 'quadrizeps', groesse: 'gross' },
  quad_aussen: { label: 'Quadrizeps, äußerer Anteil', gruppe: 'quadrizeps', groesse: 'gross' },
  beinbeuger: { label: 'Beinbeuger', gruppe: 'beinbeuger', groesse: 'gross' },
  waden_zwilling: { label: 'Zwillingswadenmuskel', gruppe: 'waden', groesse: 'mittel' },
  waden_scholle: { label: 'Schollenmuskel', gruppe: 'waden', groesse: 'mittel' },
  schienbein: { label: 'Schienbeinmuskel', gruppe: 'waden', groesse: 'klein' },
} as const satisfies Record<string, { label: string; gruppe: GruppeId; groesse: Groesse }>;

export type MuskelId = keyof typeof MUSKELN;

export const MUSKEL_IDS = Object.keys(MUSKELN) as MuskelId[];
export const GRUPPEN_IDS = Object.keys(GRUPPEN) as GruppeId[];

export const gruppeVon = (m: MuskelId): GruppeId => MUSKELN[m].gruppe;

export const muskelnDerGruppe = (g: GruppeId): MuskelId[] => MUSKEL_IDS.filter((m) => MUSKELN[m].gruppe === g);

export const istMuskel = (x: unknown): x is MuskelId => typeof x === 'string' && x in MUSKELN;

/**
 * Beitrag einer Uebung je Muskel und Satz: Haupt 1, Hilf 0,5. Steht ein
 * Muskel in beiden Listen, zaehlt er als Hauptmuskel.
 */
export function beitragJeMuskel(haupt: readonly MuskelId[], hilf: readonly MuskelId[]): Map<MuskelId, number> {
  const m = new Map<MuskelId, number>();
  for (const x of hilf) m.set(x, 0.5);
  for (const x of haupt) m.set(x, 1);
  return m;
}

/** Dasselbe je Gruppe: der hoechste Beitrag ihrer Muskeln. */
export function beitragJeGruppe(haupt: readonly MuskelId[], hilf: readonly MuskelId[]): Map<GruppeId, number> {
  const g = new Map<GruppeId, number>();
  for (const [m, w] of beitragJeMuskel(haupt, hilf)) {
    const k = gruppeVon(m);
    g.set(k, Math.max(g.get(k) ?? 0, w));
  }
  return g;
}
